import type { ResultLike } from '../rules/officialDrawResults';
import { enrichOfficialDrawPicks } from './enrichOfficialDrawPicks';

export type PlatformDrawLike = {
  source: string;
  dayKey: string;
  window: 'day' | 'night';
  first: string;
  second: string;
  third: string;
  feed?: string;
  pick3?: string | null;
  pick4?: string | null;
  pick2?: string | null;
  pick5?: string | null;
  numbers?: string | null;
  win4?: string | null;
  cash3?: string | null;
  cash4?: string | null;
  youtubeVideoId?: string | null;
  id?: string;
};

export function isPlatformLotterySource(
  source: string | null | undefined,
): source is 'florida' | 'newyork' {
  return source === 'florida' || source === 'newyork';
}

function platformDrawToResult(draw: PlatformDrawLike): ResultLike {
  const picks = enrichOfficialDrawPicks({
    source: draw.source,
    first: draw.first,
    second: draw.second,
    third: draw.third,
    pick3: draw.pick3,
    pick4: draw.pick4,
    numbers: draw.numbers,
    win4: draw.win4,
    cash3: draw.cash3,
    cash4: draw.cash4,
  });
  return {
    id: draw.id ?? `platform:${draw.source}:${draw.dayKey}:${draw.window}`,
    // Catalog / publish civil day — winsForDraw maps to acceptance via house schedule.
    dayKey: draw.dayKey,
    window: draw.window,
    first: draw.first,
    second: draw.second,
    third: draw.third,
    source: draw.source,
    feed: draw.feed === 'manual' ? 'manual' : 'auto',
    picks: {
      pick3: picks.pick3,
      pick4: picks.pick4,
      pick2: draw.pick2 ?? null,
      pick5: draw.pick5 ?? null,
    },
    youtubeVideoId: draw.youtubeVideoId ?? null,
  };
}

/**
 * Resolve the Resultados feed for a house.
 *
 * Florida / New York → platform store only (shared).
 * Manual guides → house result submissions.
 *
 * Overnight play matching happens in winsForDraw via acceptanceDayKeyForPlatformDraw.
 */
export function resolveResultsFeedForHouse(
  houseResults: ResultLike[],
  platformDraws: PlatformDrawLike[],
  source: string | null | undefined,
): ResultLike[] {
  if (isPlatformLotterySource(source)) {
    return platformDraws
      .filter((draw) => draw.source === source)
      .map(platformDrawToResult);
  }
  return houseResults;
}
